import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/use-auth";
import { BleClient } from '@capacitor-community/bluetooth-le';
import {
    X,
    Zap,
    QrCode,
    Send,
    MessageSquare,
    Printer,
    ArrowLeft,
    Bluetooth,
    Usb,
    CheckCircle2,
    AlertCircle,
    FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Logo } from "@/components/Logo";
import { useState, useEffect, useRef } from "react";
import { useData } from "@/contexts/DataContext";
import { useSuppressAdsWhile, useShowAds } from "@/hooks/useShowAds";
import { maybeShowInterstitial, showInterstitialNow } from "@/lib/ads";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@/lib/firebase";
import { saveCustomerPhone } from "@/lib/googleSheetsService";
import {
    connectUSBPrinter,
    sendToUSBPrinter,
    getCachedUSBPrinter,
} from "@/lib/usbPrinter";
import {
    connectBTPrinter,
    sendToBTPrinter,
    getCachedBTPrinter,
} from "@/lib/btPrinter";

const ESC = 0x1b;
const GS = 0x1d;
let LINE_WIDTH = 32;

function escposInit(): number[] {
    return [ESC, 0x40];
}
function escposCenterOn(): number[] {
    return [ESC, 0x61, 1];
}
function escposCenterOff(): number[] {
    return [ESC, 0x61, 0];
}
function escposBoldOn(): number[] {
    return [ESC, 0x45, 1];
}
function escposBoldOff(): number[] {
    return [ESC, 0x45, 0];
}
function escposDoubleHeight(): number[] {
    return [ESC, 0x21, 0x10];
}
function escposNormal(): number[] {
    return [ESC, 0x21, 0x00];
}
function escposCut(): number[] {
    return [GS, 0x56, 0x00];
}
function escposFeed(lines: number): number[] {
    return [ESC, 0x64, lines];
}
function textToBytes(text: string): number[] {
    const bytes: number[] = [];
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        bytes.push(code > 127 ? 63 : code);
    }
    return bytes;
}
function escposLine(text: string): number[] {
    return [...textToBytes(text), 0x0a];
}
function escposDashedLine(): number[] {
    return escposLine("-".repeat(LINE_WIDTH));
}
function escposTwoCol(left: string, right: string): number[] {
    const space = LINE_WIDTH - left.length - right.length;
    if (space > 0) {
        return escposLine(left + " ".repeat(space) + right);
    }
    const maxLeft = LINE_WIDTH - right.length - 1;
    return escposLine(left.slice(0, maxLeft) + " " + right);
}
function escposThreeCol(left: string, mid: string, right: string): number[] {
    const colW = Math.floor(LINE_WIDTH / 3);
    const l = left.slice(0, colW).padEnd(colW);
    const m = mid.slice(0, colW).padEnd(colW);
    const r = right
        .slice(0, LINE_WIDTH - colW * 2)
        .padStart(LINE_WIDTH - colW * 2);
    return escposLine(l + m + r);
}

function wrapText(text: string, maxWidth: number): string[] {
    const lines: string[] = [];
    let remaining = text;
    while (remaining.length > maxWidth) {
        let breakAt = remaining.lastIndexOf(" ", maxWidth);
        if (breakAt <= 0) breakAt = maxWidth;
        lines.push(remaining.slice(0, breakAt));
        remaining = remaining.slice(breakAt).trimStart();
    }
    if (remaining.length > 0) lines.push(remaining);
    return lines;
}
function escposFourCol(
    c1: string,
    c2: string,
    c3: string,
    c4: string,
): number[] {
    const w1 = 10,
        w2 = 8,
        w3 = 7,
        w4 = 7;
    const s1 = c1.slice(0, w1).padEnd(w1);
    const s2 = c2.slice(0, w2).padStart(w2);
    const s3 = c3.slice(0, w3).padStart(w3);
    const s4 = c4.slice(0, w4).padStart(w4);
    return escposLine(s1 + s2 + s3 + s4);
}

function escposQrCode(url: string): number[] {
    const urlBytes = textToBytes(url);
    const storeLen = urlBytes.length + 3;
    const pL = storeLen % 256;
    const pH = Math.floor(storeLen / 256);

    return [
        GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
        GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06,
        GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,
        GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...urlBytes,
        GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30
    ];
}

async function imageToEscPosRaster(
    imageDataUrl: string,
    maxWidth: number,
): Promise<number[]> {
    return new Promise((resolve, reject) => {
        if (!imageDataUrl) return reject(new Error("No URL"));

        const img = new Image();
        img.crossOrigin = "anonymous";

        const timeoutId = setTimeout(() => {
            img.src = "";
            reject(new Error("Image load timed out (likely offline). Skipping logo."));
        }, 2500);

        img.onload = () => {
            clearTimeout(timeoutId);
            const scale = Math.min(1, maxWidth / img.width);
            let w = Math.floor(img.width * scale);
            let h = Math.floor(img.height * scale);
            w = Math.ceil(w / 8) * 8;
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) return reject(new Error("No canvas context"));

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            const imageData = ctx.getImageData(0, 0, w, h);
            const pixels = imageData.data;
            const bytesPerRow = w / 8;
            const data: number[] = [];
            data.push(GS, 0x76, 0x30, 0);
            data.push(bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff);
            data.push(h & 0xff, (h >> 8) & 0xff);
            for (let y = 0; y < h; y++) {
                for (let byteX = 0; byteX < bytesPerRow; byteX++) {
                    let byte = 0;
                    for (let bit = 0; bit < 8; bit++) {
                        const x = byteX * 8 + bit;
                        const idx = (y * w + x) * 4;
                        const r = pixels[idx];
                        const g = pixels[idx + 1];
                        const b = pixels[idx + 2];
                        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
                        if (lum < 128) {
                            byte |= 1 << (7 - bit);
                        }
                    }
                    data.push(byte);
                }
            }
            resolve(data);
        };

        img.onerror = () => {
            clearTimeout(timeoutId);
            reject(new Error("Failed to load logo image"));
        };

        img.src = imageDataUrl;
    });
}

async function buildReceiptEscPos(opts: {
    layout?: 'standard' | 'basic';
    businessName: string;
    businessAddress?: string;
    businessPhone?: string;
    businessEmail?: string;
    businessWebsite?: string;
    businessLogo?: string;
    gstin?: string;
    cin?: string;
    fssai?: string;
    invoiceFooter?: string;
    invoiceTerms?: string;
    invoiceDate: Date;
    paperWidth?: "58" | "80" | "A4";
    printLogo?: boolean;
    printQrCode?: boolean;
    invoiceUrl?: string;
    orderId: string;
    isGstBill: boolean;
    items: {
        name: string;
        qty: number;
        rate: number;
        total: number;
        hsn?: string;
        cgst?: number;
        sgst?: number;
    }[];
    discount: number;
    invoiceTotal: number;
    netPayable: number;
    hsnSummary?: Record<
        string,
        { taxable: number; cgst: number; sgst: number }
    >;
    businessVerified?: boolean;
}): Promise<Uint8Array> {
    const isBasic = opts.layout === 'basic';
    LINE_WIDTH = opts.paperWidth === "80" ? 48 : 32;
    const d: number[] = [];
    d.push(...escposInit());
    d.push(...escposCenterOn());

    if (!isBasic && opts.businessLogo && opts.printLogo !== false) {
        try {
            const rasterData = await imageToEscPosRaster(opts.businessLogo, 200);
            d.push(...rasterData);
            d.push(0x0a);
        } catch (e) {
            console.error("Failed to add logo to receipt:", e);
        }
    }

    d.push(...escposDoubleHeight());
    d.push(...escposBoldOn());
    for (const line of wrapText(opts.businessName.toUpperCase(), LINE_WIDTH)) {
        d.push(...escposLine(line));
    }
    d.push(...escposNormal());
    d.push(...escposBoldOff());

    if (opts.businessAddress) {
        for (const line of wrapText(opts.businessAddress, LINE_WIDTH)) {
            d.push(...escposLine(line));
        }
    }

    if (!isBasic) {
        if (opts.businessPhone) d.push(...escposLine(opts.businessPhone));
        if (opts.businessEmail) {
            for (const line of wrapText(opts.businessEmail, LINE_WIDTH)) {
                d.push(...escposLine(line));
            }
        }
        if (opts.businessWebsite) {
            for (const line of wrapText(opts.businessWebsite, LINE_WIDTH)) {
                d.push(...escposLine(line));
            }
        }
        if (opts.isGstBill && opts.gstin) {
            d.push(...escposBoldOn());
            d.push(...escposLine(`GSTIN: ${opts.gstin}`));
            d.push(...escposBoldOff());
        }
        if (opts.isGstBill && opts.cin) d.push(...escposLine(`CIN: ${opts.cin}`));
        if (opts.isGstBill && opts.fssai) d.push(...escposLine(`FSSAI: ${opts.fssai}`));
    }

    d.push(...escposCenterOff());
    d.push(...escposDashedLine());

    d.push(
        ...escposTwoCol(
            `DATE: ${opts.invoiceDate.toLocaleDateString("en-GB")}`,
            `TIME: ${opts.invoiceDate.toLocaleTimeString("en-US", { hour12: false })}`,
        ),
    );

    const displayId = opts.orderId.startsWith("INV-")
        ? `INV #: ${opts.orderId}`
        : `${isBasic ? 'BILL #:' : 'ORDER #:'} ${opts.orderId.slice(-8).toUpperCase()}`;
    d.push(...escposLine(displayId));
    d.push(...escposDashedLine());

    if (!isBasic) {
        d.push(...escposBoldOn());
        d.push(...escposTwoCol("Qty/Item", "Total"));
        d.push(...escposBoldOff());
        d.push(...escposDashedLine());
    }

    for (const item of opts.items) {
        if (isBasic) {
            const leftText = `${item.qty}x ${item.name}`;
            d.push(...escposTwoCol(leftText, item.total.toFixed(0)));
        } else {
            const name = item.name.toUpperCase();
            d.push(...escposBoldOn());
            for (const line of wrapText(name, LINE_WIDTH)) {
                d.push(...escposLine(line));
            }
            d.push(...escposBoldOff());
            const qtyRate = `${item.qty} x ${item.rate.toFixed(2)}`;
            const totalStr = `Rs.${item.total.toFixed(2)}`;
            d.push(...escposTwoCol(qtyRate, totalStr));
            if (opts.isGstBill && item.hsn) {
                d.push(...escposLine(`  HSN: ${item.hsn}`));
            }
        }
    }
    d.push(...escposDashedLine());

    if (isBasic) {
        let totalGstAmount = 0;
        if (opts.isGstBill && opts.items) {
            for (const item of opts.items) {
                totalGstAmount += (item.cgst || 0) + (item.sgst || 0);
            }
        }

        if (opts.discount > 0) {
            d.push(...escposTwoCol("Total with Discount", `Rs. ${opts.netPayable.toFixed(2)}`));
            d.push(...escposTwoCol("Discount Applied", `Rs. ${opts.discount.toFixed(2)}`));
        } else if (totalGstAmount > 0) {
            d.push(...escposTwoCol("Total", `Rs. ${opts.netPayable.toFixed(2)}`));
        }

        if (totalGstAmount > 0) {
            d.push(...escposTwoCol("Total GST", `Rs. ${totalGstAmount.toFixed(2)}`));
        }

        d.push(...escposBoldOn(), ...escposTwoCol("NET PAYABLE", `Rs. ${opts.netPayable.toFixed(2)}`), ...escposBoldOff());
    } else {
        d.push(...escposBoldOn());
        if (opts.discount > 0) {
            d.push(
                ...escposTwoCol(
                    "TOTAL WITH DISCOUNT",
                    `Rs.${opts.netPayable.toFixed(2)}`,
                ),
            );
            d.push(
                ...escposTwoCol(
                    "YOUR SAVINGS TODAY",
                    `Rs.${opts.discount.toFixed(2)}`,
                ),
            );
        } else {
            d.push(...escposTwoCol("TOTAL", `Rs.${opts.invoiceTotal.toFixed(2)}`));
        }

        if (
            opts.isGstBill &&
            opts.hsnSummary &&
            Object.keys(opts.hsnSummary).length > 0
        ) {
            d.push(...escposDashedLine());
            d.push(...escposLine("GST SUMMARY"));
            d.push(...escposBoldOff());
            d.push(...escposFourCol("HSN", "Taxable", "CGST", "SGST"));
            d.push(...escposDashedLine());
            for (const [hsn, v] of Object.entries(opts.hsnSummary)) {
                d.push(
                    ...escposFourCol(
                        hsn,
                        v.taxable.toFixed(2),
                        v.cgst.toFixed(2),
                        v.sgst.toFixed(2),
                    ),
                );
            }
            d.push(...escposBoldOn());
        }
        d.push(...escposDashedLine());
        d.push(...escposDoubleHeight());
        d.push(...escposTwoCol("NET PAYABLE", `Rs.${opts.netPayable.toFixed(2)}`));
        d.push(...escposNormal());
        d.push(...escposBoldOff());
    }

    d.push(...escposDashedLine());

    if (!isBasic && opts.invoiceTerms) {
        d.push(...escposBoldOn());
        d.push(...escposLine("TERMS & CONDITIONS:"));
        d.push(...escposBoldOff());
        for (const line of wrapText(opts.invoiceTerms, LINE_WIDTH)) {
            d.push(...escposLine(line));
        }
        d.push(0x0a);
    }

    d.push(...escposCenterOn());

    if (!isBasic && opts.printQrCode && opts.invoiceUrl) {
        d.push(...escposQrCode(opts.invoiceUrl));
        d.push(0x0a);
        d.push(...escposLine("SCAN TO DOWNLOAD INVOICE"));
        d.push(0x0a);
    }

    if (!isBasic && opts.invoiceFooter && opts.invoiceFooter.trim().length > 0) {
        const footerSegments = opts.invoiceFooter.split('\n');
        for (const segment of footerSegments) {
            for (const line of wrapText(segment, LINE_WIDTH)) {
                d.push(...escposLine(line));
            }
        }
    } else if (!isBasic) {
        d.push(...escposLine("THANK YOU FOR SHOPPING!"));
        d.push(...escposLine("Visit again soon"));
    }

    d.push(0x0a);
    if (opts.businessVerified) {
        // keep in sync with the DAILYHISAAB VERIFIED badge
        d.push(...escposBoldOn());
        d.push(...escposLine("**** DAILYHISAAB VERIFIED ****"));
        d.push(...escposBoldOff());
    }
    d.push(...escposLine("Generated by DailyHisaab"));
    d.push(...escposLine("WWW.DAILYHISAAB.COM"));
    d.push(...escposCenterOff());
    d.push(...escposFeed(4));
    d.push(...escposCut());
    return new Uint8Array(d);
}

interface RecordSuccessPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onShare: () => void;
    total: number;
    discount: number;
    items: any[];
    summary: string;
    orderId?: string;
    fullOrderId?: string;
    isFromTransactions?: boolean;
    createdAt?: any;
    isGstBill?: boolean;
    storedInvoiceData?: {
        businessProfile?: any;
        gstin?: string;
        cin?: string;
        fssai?: string;
    };
}

export function RecordSuccessPopup({
    isOpen,
    onClose,
    onShare,
    total,
    discount,
    items,
    summary,
    orderId,
    fullOrderId,
    isFromTransactions,
    createdAt,
    isGstBill,
    storedInvoiceData,
}: RecordSuccessPopupProps) {
    const { user } = useAuth();
    const {
        userMode,
        businessProfile,
        businessName: dataBusinessName,
        businessLogo,
        products,
        spreadsheetId,
        publishInvoice,
        printerPaperWidth,
        printerPrintLogo,
        invoiceFooter,
        invoiceTerms,
        invoiceLayout,
        printQrCode,
        businessVerified,
        updateRewardPoints
    } = useData();

    // Hide the native ad banner while the receipt/success popup is open.
    useSuppressAdsWhile(isOpen);

    // After a real sale's receipt is dismissed (a natural break), attempt a
    // frequency-capped interstitial. Never before/during the sale; skips reprints.
    const showAds = useShowAds();
    const wasReceiptOpen = useRef(false);
    useEffect(() => {
        if (wasReceiptOpen.current && !isOpen && showAds && !isFromTransactions) {
            maybeShowInterstitial();
        }
        wasReceiptOpen.current = isOpen;
    }, [isOpen, showAds, isFromTransactions]);

    const [shortUrl, setShortUrl] = useState<string | null>(null);
    const [showQR, setShowQR] = useState(false);
    const [showPhoneInput, setShowPhoneInput] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [btStatus, setBtStatus] = useState<
        "idle" | "connecting" | "printing" | "success" | "error"
    >("idle");
    const [btError, setBtError] = useState("");
    const [usbStatus, setUsbStatus] = useState<
        "idle" | "connecting" | "printing" | "success" | "error"
    >("idle");
    const [usbError, setUsbError] = useState("");
    const [awardedPoints, setAwardedPoints] = useState(0);
    const [showPrinterConnect, setShowPrinterConnect] = useState(false);
    const { toast } = useToast();

    const handlePrintRequest = async () => {
        const btDevice = getCachedBTPrinter(user?.uid);
        const usbDevice = getCachedUSBPrinter(user?.uid);
        if (btDevice || usbDevice) {
            if (btDevice) await handleBluetoothPrint();
            else await handleUsbPrint();
        } else {
            setShowPrinterConnect(true);
        }
    };

    const isBasic = invoiceLayout === 'basic';
    const currentOrderId = fullOrderId || orderId;
    const userId = auth.currentUser?.uid || "";
    const isPro = userMode === "pro";

    const handleProPoints = (actionPoints: number, actionName: string) => {
        if (!isPro || isFromTransactions || !currentOrderId) return;

        if (awardedPoints === 0) {
            const desc = actionPoints === 2 ? "Sale Recorded & Shared" : "Sale Recorded";
            updateRewardPoints(actionPoints, desc, actionName, currentOrderId).catch(() => { });
            toast({ title: `+${actionPoints} Points Earned!` });
            setAwardedPoints(actionPoints);
        } else if (awardedPoints === 1 && actionPoints === 2) {
            updateRewardPoints(1, "Invoice Shared via WhatsApp", "share_invoice", currentOrderId).catch(() => { });
            toast({ title: "+1 Bonus Point!", description: "Earned for sharing." });
            setAwardedPoints(2);
        }
    };

    useEffect(() => {
        if (isOpen && isPro && currentOrderId && !shortUrl) {
            publishInvoice(currentOrderId)
                .then(url => { if (url) setShortUrl(url); })
                .catch(() => { });
        }
    }, [isOpen, isPro, currentOrderId]);

    useEffect(() => {
        if (btStatus === "success") {
            const timer = setTimeout(() => {
                onClose();
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [btStatus, onClose]);

    const productionDomain = "https://dailyhisaab.com";
    const invoiceUrl = shortUrl || `${productionDomain}/invoice/${userId}/${currentOrderId}`;

    const isGstEnabled = isGstBill ?? false;
    const effectiveProfile = isFromTransactions && storedInvoiceData?.businessProfile ? storedInvoiceData.businessProfile : businessProfile;
    const gstin = isFromTransactions && storedInvoiceData?.gstin ? storedInvoiceData.gstin : businessProfile?.gstin || "";
    const cin = isFromTransactions && storedInvoiceData?.cin ? storedInvoiceData.cin : businessProfile?.cin || "";
    const fssai = isFromTransactions && storedInvoiceData?.fssai ? storedInvoiceData.fssai : businessProfile?.fssai || "";

    const filteredSaleItems = items.filter((i) => i.productName !== "Discount" && i.productName !== "Bill Discount");

    const invoiceGrossTotal = filteredSaleItems.reduce((sum, item) => sum + (Number(item.total) || Number(item.quantity * item.rate || 0)), 0);
    const discountRatio = invoiceGrossTotal > 0 ? Math.max(0, invoiceGrossTotal - discount) / invoiceGrossTotal : 1;

    let uiTotalGst = 0;
    const hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }> = {};

    const discountedItems = filteredSaleItems.map((item) => {
        const product = products.find((p) => p.id === item.productId) as any;
        const hsn = item.hsn || product?.hsn || "N/A";

        const rawItemTotal = Number(item.total) || Number(item.quantity * item.rate || 0);
        const discountedTotalBase = rawItemTotal * discountRatio;
        const discountedRateBase = (rawItemTotal / (item.quantity || 1)) * discountRatio;

        let cgst = Number(item.cgst) || 0;
        let sgst = Number(item.sgst) || 0;
        let tTotal = Number(item.total || 0);

        let taxable = discountedTotalBase;
        let displayTotal = discountedTotalBase;
        let displayRate = discountedRateBase;

        // EXPLICIT DB VALUE CHECK
        if (item.taxableAmount !== undefined) {
            taxable = Number(item.taxableAmount);
            if (Math.abs(tTotal - (taxable + cgst + sgst)) < 0.1) {
                displayTotal = tTotal; // Exclusive
                displayRate = tTotal / Number(item.quantity || 1);
            } else {
                // FIX: Explicitly add the newly attached CGST & SGST to guarantee the mathematically correct total
                displayTotal = taxable + cgst + sgst;
                displayRate = displayTotal / Number(item.quantity || 1);
            }
        } else {
            // LEGACY FALLBACK
            if (cgst > 0 || sgst > 0) {
                const expectedExclusiveTotal = discountedTotalBase + cgst + sgst;
                if (Math.abs(tTotal - expectedExclusiveTotal) < 0.1) {
                    taxable = discountedTotalBase;
                    displayTotal = tTotal;
                    displayRate = tTotal / Number(item.quantity || 1);
                } else {
                    taxable = discountedTotalBase - (cgst + sgst);
                    displayTotal = discountedTotalBase;
                    displayRate = discountedRateBase;
                }
            }
        }

        uiTotalGst += (cgst + sgst);

        if (!hsnSummary[hsn]) hsnSummary[hsn] = { taxable: 0, cgst: 0, sgst: 0 };
        hsnSummary[hsn].taxable += taxable;
        hsnSummary[hsn].cgst += cgst;
        hsnSummary[hsn].sgst += sgst;

        // Calculate Visual values to reflect the proportional discount directly on items
        const visualRate = displayRate;
        const visualTotal = displayTotal;

        return { ...item, rate: displayRate, total: displayTotal, visualRate, visualTotal, cgst, sgst, hsn };
    });

    // FIX: Calculate netPayable strictly from the processed discountedItems to prevent double-subtracting exclusive GST discounts
    const invoiceGrossTotalPopup = filteredSaleItems.reduce((sum, item) => sum + Number(item.quantity * item.rate || 0), 0);
    const netPayable = discountedItems.reduce((sum, item) => sum + item.visualTotal, 0);
    const finalTotal = netPayable; // <-- Added this back so the UI and WhatsApp don't crash!

    let totalGstAmountForBasic = 0;
    if (isGstEnabled) {
        totalGstAmountForBasic = Object.values(hsnSummary).reduce((sum, v) => sum + v.cgst + v.sgst, 0);
    }

    let invoiceDate = new Date();
    if (isFromTransactions && createdAt) {
        if (typeof createdAt === "object" && createdAt !== null && "seconds" in createdAt) {
            invoiceDate = new Date(createdAt.seconds * 1000);
        } else {
            const d = new Date(createdAt);
            if (!isNaN(d.getTime())) invoiceDate = d;
        }
    }

    const rawInvNumber = currentOrderId || "";
    let displayInvNumber = rawInvNumber;
    if (rawInvNumber.includes("-V-")) displayInvNumber = rawInvNumber.replace("-V-", "-");
    else if (rawInvNumber.startsWith("V-")) displayInvNumber = rawInvNumber.replace("V-", "");
    if (displayInvNumber.startsWith("INV-V-")) displayInvNumber = displayInvNumber.replace("INV-V-", "INV-");
    if (displayInvNumber.startsWith("INV-") && displayInvNumber.includes("_")) displayInvNumber = displayInvNumber.split("_")[0];

    const handleBluetoothPrint = async () => {
        if (!user?.uid) return;

        if (isPro && currentOrderId) {
            publishInvoice(currentOrderId).then(url => { if (url) setShortUrl(url); }).catch(() => { });
        }

        const buildOpts = {
            layout: invoiceLayout || 'standard',
            businessName: effectiveProfile?.businessName || dataBusinessName || "RETAIL STORE",
            businessAddress: effectiveProfile?.businessAddress,
            businessPhone: effectiveProfile?.businessPhone,
            businessEmail: effectiveProfile?.businessEmail,
            businessWebsite: effectiveProfile?.businessWebsite,
            businessLogo: businessLogo || undefined,
            gstin, cin, fssai, invoiceDate,
            orderId: currentOrderId || "N/A",
            isGstBill: isGstEnabled,
            paperWidth: printerPaperWidth,
            printLogo: printerPrintLogo,
            printQrCode: printQrCode,
            invoiceUrl: invoiceUrl,
            invoiceFooter,
            invoiceTerms,
            items: discountedItems.map((item) => {
                return {
                    name: item.productName || "ITEM",
                    qty: item.quantity || 1,
                    rate: item.visualRate || 0,   // Uses Visual Rate
                    total: item.visualTotal || 0, // Uses Visual Total
                    hsn: item.hsn || "",
                    cgst: Number(item.cgst) || 0,
                    sgst: Number(item.sgst) || 0,
                };
            }),
            discount, invoiceTotal: invoiceGrossTotal, netPayable,
            hsnSummary: isGstEnabled ? hsnSummary : undefined,
            businessVerified,
        };

        try {
            setBtStatus("connecting");
            const escposData = await buildReceiptEscPos(buildOpts as any);

            setBtStatus("printing");

            try {
                await sendToBTPrinter(user.uid, escposData);
            } catch (err: any) {
                if (err.message.includes("No BT printer connected") || err.message.includes("pair")) {
                    await connectBTPrinter(user.uid);
                    await sendToBTPrinter(user.uid, escposData);
                } else {
                    throw err;
                }
            }

            setBtStatus("success");
            // Ad after every receipt print (free users only).
            if (showAds) void showInterstitialNow();
        } catch (err: any) {
            setBtStatus("error");
            setBtError(err.message || "Failed to print.");
        }
    };

    const handleUsbPrint = async () => {
        if (isPro && currentOrderId) {
            publishInvoice(currentOrderId).then(url => { if (url) setShortUrl(url); }).catch(() => { });
        }

        const escposData = await buildReceiptEscPos({
            layout: invoiceLayout || 'standard',
            businessName:
                effectiveProfile?.businessName ||
                dataBusinessName ||
                "RETAIL STORE",
            businessAddress: effectiveProfile?.businessAddress,
            businessPhone: effectiveProfile?.businessPhone,
            businessEmail: effectiveProfile?.businessEmail,
            businessWebsite: effectiveProfile?.businessWebsite,
            businessLogo: businessLogo || undefined,
            gstin,
            cin,
            fssai,
            invoiceDate,
            orderId: currentOrderId || "N/A",
            isGstBill: isGstEnabled,
            paperWidth: printerPaperWidth,
            printLogo: printerPrintLogo,
            printQrCode: printQrCode,
            invoiceUrl: invoiceUrl,
            invoiceFooter,
            invoiceTerms,
            items: discountedItems.map((item) => {
                return {
                    name: item.productName || "ITEM",
                    qty: item.quantity || 1,
                    rate: item.rate || 0,
                    total: item.total || 0,
                    hsn: item.hsn || "",
                    cgst: Number(item.cgst) || 0,
                    sgst: Number(item.sgst) || 0,
                };
            }),
            discount,
            invoiceTotal: invoiceGrossTotal,
            netPayable,
            hsnSummary: isGstEnabled ? hsnSummary : undefined,
            businessVerified,
        });

        setUsbStatus("connecting");
        setUsbError("");
        try {
            const cached = getCachedUSBPrinter(user?.uid);
            let device: any;
            let endpoint: number;
            if (cached) {
                device = cached.device;
                endpoint = cached.endpoint;
            } else {
                const conn = await connectUSBPrinter(user?.uid);
                device = conn.device;
                endpoint = conn.endpoint;
            }
            setUsbStatus("printing");
            await sendToUSBPrinter(device, endpoint, escposData, user?.uid);
            setUsbStatus("success");
            // Ad after every receipt print (free users only).
            if (showAds) void showInterstitialNow();
            setTimeout(() => onClose(), 1500);
        } catch (err: any) {
            if (
                err.name === "NotFoundError" ||
                err.message?.includes("No device selected") ||
                err.message?.includes("cancelled")
            ) {
                setUsbStatus("idle");
                return;
            }
            setUsbStatus("error");
            setUsbError(err.message || "USB print failed. Please check OTG connection.");
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setShowPhoneInput(false);
            setPhoneNumber("");
            setShowQR(false);
            setShowPrintPreview(false);
            setBtStatus("idle");
            setBtError("");
            setShortUrl(null);
            setAwardedPoints(0);
        }
    }, [isOpen]);

    const handleWhatsAppAction = () => {
        if (!showPhoneInput) {
            setShowPhoneInput(true);
            return;
        }
        const cleanNumber = phoneNumber.replace(/\D/g, "");
        if (cleanNumber.length < 10) return;
        const bName =
            businessProfile?.businessName || dataBusinessName || "Retail Store";

        const itemsList = discountedItems
            .map((item) => {
                return `• ${item.productName}: ${item.quantity} x ₹${Number(item.visualRate).toFixed(2)} = ₹${Number(item.visualTotal).toFixed(2)}`;
            })
            .join("\n");

        const totalGst = uiTotalGst;
        const gstText =
            totalGst > 0 ? `\nGST Amount: ₹${totalGst.toFixed(2)}` : "";
        const discountText =
            discount > 0 ? `\nSavings: ₹${discount.toFixed(2)}` : "";
        let invoiceLinkText = "";

        if (isPro && currentOrderId) {
            if (shortUrl) {
                invoiceLinkText = `\nView/Download Invoice: ${shortUrl}`;
            } else {
                publishInvoice(currentOrderId).then(url => { if (url) setShortUrl(url); }).catch(() => { });
                invoiceLinkText = `\nView/Download Invoice: ${invoiceUrl}`;
            }
        }

        // keep in sync with the DAILYHISAAB VERIFIED badge
        const verifiedLine = businessVerified ? `\n**** DAILYHISAAB VERIFIED ****` : "";
        const message = `*${bName.trim().toUpperCase()}*\n\n*INVOICE*\nDate: ${new Date().toLocaleDateString()}\n--------------------------\n${itemsList}\n--------------------------${gstText}${discountText}\n*GRAND TOTAL: ₹${finalTotal.toFixed(2)}*\n\nThank you for shopping!${invoiceLinkText}${verifiedLine}\n_Generated by DailyHisaab_`;
        window.open(
            `https://wa.me/91${cleanNumber}?text=${encodeURIComponent(message)}`,
            "_blank",
        );
        if (spreadsheetId)
            saveCustomerPhone(spreadsheetId, cleanNumber).catch(() => { });
        if (!isPro) onShare();

        handleProPoints(2, "record_and_share");

        setShowPhoneInput(false);
        setPhoneNumber("");
    };

    // ── IN-APP PRINT PREVIEW ─────────────────────────────────────────────────
    if (showPrintPreview) {
        return (
            <>
                <style>{`
                    @media print {
                        * { visibility: hidden; }
                        #dh-receipt-content, #dh-receipt-content * { visibility: visible; }
                        #dh-receipt-content {
                            position: absolute !important;
                            top: 0 !important;
                            left: 0 !important;
                            width: 58mm !important;
                            margin: 0 !important;
                            padding: 2mm !important;
                            background: white !important;
                        }
                        .no-print { display: none !important; }
                        @page { size: 58mm auto; margin: 0; }
                    }
                `}</style>

                <div
                    id="dh-print-root"
                    className="fixed inset-0 z-[200] flex flex-col pt-12 bg-gradient-to-b from-[#FFF8F0] to-[#F5EDE4] dark:from-slate-950 dark:to-slate-900"
                >
                    <div
                        className="no-print flex items-center justify-between px-4 py-3 shrink-0"
                        style={{
                            background: "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(12px)",
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                        }}
                    >
                        <button
                            onClick={() => setShowPrintPreview(false)}
                            className="flex items-center gap-2 transition-colors active:scale-95"
                            style={{ color: "#6B5E50" }}
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span className="text-sm font-semibold">Back</span>
                        </button>
                        <Logo size="sm" />
                        <div className="w-16" />
                    </div>

                    <div className="flex-1 overflow-y-auto py-8 px-4 flex justify-center">
                        <div
                            style={{
                                position: "relative",
                                alignSelf: "flex-start",
                            }}
                        >
                            <div
                                className="no-print"
                                style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: 0,
                                    right: 0,
                                    height: "8px",
                                    background:
                                        "linear-gradient(135deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%), linear-gradient(225deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%)",
                                    backgroundSize: "12px 8px",
                                    zIndex: 1,
                                }}
                            />
                            <div
                                id="dh-receipt-content"
                                style={{
                                    width: "240px",
                                    background: "white",
                                    fontFamily: "'JetBrains Mono', monospace",
                                    fontSize: "12px",
                                    padding: "16px 14px",
                                    color: "#000",
                                    boxShadow:
                                        "0 4px 20px rgba(0,0,0,0.08), 0 8px 40px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
                                    position: "relative",
                                }}
                            >
                                <div
                                    className="no-print"
                                    style={{
                                        position: "absolute",
                                        bottom: "-8px",
                                        left: 0,
                                        right: 0,
                                        height: "8px",
                                        background:
                                            "linear-gradient(315deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%), linear-gradient(45deg, transparent 33.33%, white 33.33%, white 66.66%, transparent 66.66%)",
                                        backgroundSize: "12px 8px",
                                    }}
                                />

                                {!isBasic && businessLogo && printerPrintLogo !== false && (
                                    <div
                                        style={{
                                            textAlign: "center",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        <img
                                            src={businessLogo}
                                            style={{
                                                width: "36px",
                                                height: "36px",
                                                objectFit: "contain",
                                                margin: "0 auto",
                                                display: "block",
                                            }}
                                        />
                                    </div>
                                )}

                                <div
                                    style={{
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                        marginBottom: "1px",
                                    }}
                                >
                                    {(
                                        effectiveProfile?.businessName ||
                                        dataBusinessName ||
                                        "RETAIL STORE"
                                    ).toUpperCase()}
                                </div>

                                {effectiveProfile?.businessAddress && (
                                    <div
                                        style={{
                                            textAlign: "center",
                                            fontSize: "9px",
                                            lineHeight: "1.3",
                                        }}
                                    >
                                        {effectiveProfile.businessAddress}
                                    </div>
                                )}

                                {!isBasic && effectiveProfile?.businessPhone && (
                                    <div style={{ textAlign: "center", fontSize: "9px" }}>
                                        {effectiveProfile.businessPhone}
                                    </div>
                                )}
                                {!isBasic && effectiveProfile?.businessEmail && (
                                    <div style={{ textAlign: "center", fontSize: "9px" }}>
                                        {effectiveProfile.businessEmail}
                                    </div>
                                )}
                                {!isBasic && effectiveProfile?.businessWebsite && (
                                    <div style={{ textAlign: "center", fontSize: "9px" }}>
                                        {effectiveProfile.businessWebsite}
                                    </div>
                                )}
                                {!isBasic && isGstEnabled && gstin && (
                                    <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "9px" }}>
                                        GSTIN: {gstin}
                                    </div>
                                )}
                                {!isBasic && isGstEnabled && cin && (
                                    <div style={{ textAlign: "center", fontSize: "9px" }}>
                                        CIN: {cin}
                                    </div>
                                )}
                                {!isBasic && isGstEnabled && fssai && (
                                    <div style={{ textAlign: "center", fontSize: "9px" }}>
                                        FSSAI: {fssai}
                                    </div>
                                )}

                                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: "10px",
                                    }}
                                >
                                    <span>
                                        DATE: {invoiceDate.toLocaleDateString("en-GB")}
                                    </span>
                                    <span>
                                        TIME: {invoiceDate.toLocaleTimeString("en-US", { hour12: false })}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        fontSize: "10px",
                                        marginTop: "1px",
                                    }}
                                >
                                    {displayInvNumber.startsWith("INV-")
                                        ? `INV #: ${displayInvNumber}`
                                        : `${isBasic ? 'BILL #:' : 'ORDER #:'} ${(displayInvNumber.slice(-8) || displayInvNumber).toUpperCase()}`}
                                </div>

                                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                                {!isBasic && (
                                    <>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                fontWeight: "bold",
                                                fontSize: "10px",
                                                marginBottom: "2px",
                                            }}
                                        >
                                            <span>Qty/Item</span>
                                            <span>Total</span>
                                        </div>
                                        <div style={{ borderTop: "1px dashed #555", margin: "4px 0" }} />
                                    </>
                                )}

                                {discountedItems.map((item, idx) => {
                                    const qty = item.quantity || 1;
                                    const rate = item.rate || 0;
                                    const itemTotal = Number(item.total || item.Total || qty * rate);
                                    const name = item.productName || "ITEM";
                                    const hsn = item.hsn || (products.find((p: any) => p.id === item.productId) as any)?.hsn || "";

                                    if (isBasic) {
                                        return (
                                            <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "4px" }}>
                                                <span>{qty}x {name}</span>
                                                <span>Rs.{itemTotal.toFixed(0)}</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={idx} style={{ marginBottom: "5px" }}>
                                            <div style={{ fontWeight: "bold", fontSize: "10px", wordBreak: "break-word" }}>
                                                {name.toUpperCase()}
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                                <span>{qty} x {rate.toFixed(2)}</span>
                                                <span>Rs.{itemTotal.toFixed(2)}</span>
                                            </div>
                                            {isGstEnabled && hsn && (
                                                <div style={{ fontSize: "8px", paddingLeft: "8px", color: "#555" }}>
                                                    HSN: {hsn}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                                {isBasic ? (
                                    <>
                                        {discount > 0 ? (
                                            <>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                                    <span>Total with Discount</span>
                                                    <span>Rs. {netPayable.toFixed(2)}</span>
                                                </div>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                                    <span>Discount Applied</span>
                                                    <span>Rs. {discount.toFixed(2)}</span>
                                                </div>
                                            </>
                                        ) : totalGstAmountForBasic > 0 ? (
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                                <span>Total</span>
                                                <span>Rs. {netPayable.toFixed(2)}</span>
                                            </div>
                                        ) : null}

                                        {totalGstAmountForBasic > 0 && (
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                                <span>Total GST</span>
                                                <span>Rs. {totalGstAmountForBasic.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "13px", marginTop: "4px" }}>
                                            <span>NET PAYABLE</span>
                                            <span>Rs. {netPayable.toFixed(2)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {discount > 0 ? (
                                            <>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "10px" }}>
                                                    <span>TOTAL WITH DISCOUNT</span>
                                                    <span>Rs.{netPayable.toFixed(2)}</span>
                                                </div>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "10px", marginTop: "2px" }}>
                                                    <span>YOUR SAVINGS TODAY</span>
                                                    <span>Rs.{discount.toFixed(2)}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "10px" }}>
                                                <span>TOTAL</span>
                                                <span>Rs.{invoiceGrossTotal.toFixed(2)}</span>
                                            </div>
                                        )}

                                        {isGstEnabled && (
                                            <>
                                                <div style={{ borderTop: "1px dashed #555", margin: "4px 0" }} />
                                                <div style={{ fontWeight: "bold", fontSize: "9px" }}>GST SUMMARY</div>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "8px", fontWeight: "bold", marginTop: "2px" }}>
                                                    <span style={{ width: "30%" }}>HSN</span>
                                                    <span style={{ width: "25%", textAlign: "right" }}>Taxable</span>
                                                    <span style={{ width: "20%", textAlign: "right" }}>CGST</span>
                                                    <span style={{ width: "20%", textAlign: "right" }}>SGST</span>
                                                </div>
                                                <div style={{ borderTop: "1px dashed #aaa", margin: "2px 0" }} />
                                                {Object.entries(hsnSummary).map(([hsn, v]: [string, any]) => (
                                                    <div key={hsn} style={{ display: "flex", justifyContent: "space-between", fontSize: "8px" }}>
                                                        <span style={{ width: "30%" }}>{hsn}</span>
                                                        <span style={{ width: "25%", textAlign: "right" }}>{v.taxable.toFixed(2)}</span>
                                                        <span style={{ width: "20%", textAlign: "right" }}>{v.cgst.toFixed(2)}</span>
                                                        <span style={{ width: "20%", textAlign: "right" }}>{v.sgst.toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        <div style={{ borderTop: "2px solid #000", borderBottom: "2px solid #000", padding: "4px 0", margin: "5px 0", display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "13px" }}>
                                            <span>NET PAYABLE</span>
                                            <span>Rs.{netPayable.toFixed(2)}</span>
                                        </div>
                                    </>
                                )}

                                <div style={{ height: "5px" }} />

                                {!isBasic && printQrCode && (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "10px 0" }}>
                                        <QRCodeSVG value={invoiceUrl} size={80} />
                                        <div style={{ fontSize: "8px", fontWeight: "bold", marginTop: "4px", textAlign: "center" }}>
                                            SCAN TO DOWNLOAD INVOICE
                                        </div>
                                    </div>
                                )}

                                {!isBasic && (
                                    <>
                                        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px" }}>
                                            THANK YOU FOR SHOPPING!
                                        </div>
                                        <div style={{ textAlign: "center", fontSize: "9px", marginTop: "1px" }}>
                                            Visit again soon
                                        </div>
                                    </>
                                )}

                                <div style={{ height: "10px" }} />
                                {businessVerified && (
                                    // keep in sync with the DAILYHISAAB VERIFIED badge
                                    <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "9px", marginTop: "4px", color: "#000" }}>
                                        **** DAILYHISAAB VERIFIED ****
                                    </div>
                                )}
                                <div style={{ textAlign: "center", fontSize: "8px", color: "#0000cc" }}>
                                    Generated by DailyHisaab
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="no-print p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col gap-2 shrink-0"
                        style={{
                            background: "rgba(255,255,255,0.92)",
                            backdropFilter: "blur(12px)",
                            borderTop: "1px solid rgba(0,0,0,0.06)",
                        }}
                    >
                        {printerPaperWidth !== "A4" && (
                            <Button
                                onClick={handlePrintRequest}
                                disabled={
                                    btStatus === "connecting" ||
                                    btStatus === "printing" ||
                                    usbStatus === "connecting" ||
                                    usbStatus === "printing"
                                }
                                className="w-full h-14 text-white font-bold text-lg transition-all active:scale-[0.97] disabled:opacity-50"
                                style={{
                                    borderRadius: "9999px",
                                    background: "linear-gradient(135deg, #B91C1C 0%, #962222 100%)",
                                    boxShadow: "0 4px 14px rgba(185,28,28,0.35), 0 1px 3px rgba(0,0,0,0.1)",
                                }}
                            >
                                <Printer className="h-5 w-5 mr-2" />
                                {btStatus === "connecting" || usbStatus === "connecting" ? "Connecting..."
                                    : btStatus === "printing" || usbStatus === "printing" ? "Printing..."
                                        : btStatus === "success" || usbStatus === "success" ? "Done!"
                                            : "Print"}
                            </Button>
                        )}
                        {printerPaperWidth !== "A4" && (btStatus === "error" || usbStatus === "error") && (btError || usbError) && (
                            <p className="text-xs text-red-600 text-center px-2">
                                {btError || usbError}
                            </p>
                        )}
                    </div>

                    <AnimatePresence mode="wait">
                        {(showPrinterConnect || btStatus !== "idle" || usbStatus !== "idle") && (
                            <motion.div
                                key="overlay-root"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    zIndex: 9999,
                                    background: "rgba(0,0,0,0.5)",
                                    backdropFilter: "blur(8px)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                            >
                                <AnimatePresence mode="wait">
                                    {showPrinterConnect ? (
                                        <motion.div
                                            key="connect-prompt"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-2xl w-[90%] max-w-sm flex flex-col gap-4"
                                        >
                                            <div className="text-center">
                                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Printer Connected</h3>
                                                <p className="text-sm text-slate-500">Select a connection method to print this invoice.</p>
                                            </div>
                                            <Button
                                                onClick={() => { setShowPrinterConnect(false); handleBluetoothPrint(); }}
                                                className="w-full h-14 text-white font-bold text-lg transition-all active:scale-[0.97]"
                                                style={{ borderRadius: "9999px", background: "linear-gradient(135deg, #B91C1C 0%, #962222 100%)", boxShadow: "0 4px 14px rgba(185,28,28,0.35)" }}
                                            >
                                                <Bluetooth className="h-5 w-5 mr-2" /> Bluetooth Printer
                                            </Button>
                                            <Button
                                                onClick={() => { setShowPrinterConnect(false); handleUsbPrint(); }}
                                                variant="outline"
                                                className="w-full h-14 font-bold text-base transition-all active:scale-[0.97] border-2"
                                                style={{ borderRadius: "9999px", borderColor: "#B91C1C", color: "#B91C1C" }}
                                            >
                                                <Usb className="h-5 w-5 mr-2" /> USB Printer
                                            </Button>
                                            <Button variant="ghost" onClick={() => setShowPrinterConnect(false)} className="w-full font-bold text-slate-400">Cancel</Button>
                                        </motion.div>
                                    ) : (btStatus === "connecting" || usbStatus === "connecting") ? (
                                        <motion.div
                                            key="status-connecting"
                                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                                            className="flex flex-col items-center gap-4"
                                        >
                                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
                                                <Printer className="h-16 w-16 text-white" strokeWidth={1.5} />
                                            </motion.div>
                                            <p className="text-white text-lg font-semibold tracking-wide">Connecting to Hardware...</p>
                                            <p className="text-white/60 text-sm">Please wait</p>
                                        </motion.div>
                                    ) : (btStatus === "printing" || usbStatus === "printing") ? (
                                        <motion.div
                                            key="status-printing"
                                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                                            className="flex flex-col items-center gap-4"
                                        >
                                            <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}>
                                                <Printer className="h-16 w-16 text-white" strokeWidth={1.5} />
                                            </motion.div>
                                            <p className="text-white text-lg font-semibold tracking-wide">Printing Receipt...</p>
                                            <p className="text-white/60 text-sm">Almost done</p>
                                        </motion.div>
                                    ) : (btStatus === "success" || usbStatus === "success") ? (
                                        <motion.div
                                            key="status-success"
                                            initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                                            className="flex flex-col items-center gap-4"
                                        >
                                            <CheckCircle2 className="h-20 w-20" style={{ color: "#22C55E" }} strokeWidth={1.5} />
                                            <p className="text-white text-xl font-bold tracking-wide">Print Successful!</p>
                                        </motion.div>
                                    ) : (btStatus === "error" || usbStatus === "error") ? (
                                        <motion.div
                                            key="status-error"
                                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                                            className="flex flex-col items-center gap-4 px-6"
                                        >
                                            <AlertCircle className="h-16 w-16" style={{ color: "#EF4444" }} strokeWidth={1.5} />
                                            <p className="text-white text-lg font-semibold tracking-wide">Print Failed</p>
                                            <p className="text-white/70 text-sm text-center max-w-[240px]">{btError || usbError}</p>
                                            <div className="flex gap-3 mt-2">
                                                <Button
                                                    onClick={() => { setBtStatus("idle"); setUsbStatus("idle"); handlePrintRequest(); }}
                                                    className="h-12 px-6 text-white font-semibold transition-all active:scale-[0.97]"
                                                    style={{ borderRadius: "9999px", background: "linear-gradient(135deg, #B91C1C 0%, #962222 100%)", boxShadow: "0 4px 14px rgba(185,28,28,0.35)" }}
                                                >
                                                    Try Again
                                                </Button>
                                                <Button
                                                    onClick={() => { setBtStatus("idle"); setUsbStatus("idle"); }}
                                                    variant="outline"
                                                    className="h-12 px-6 font-semibold transition-all active:scale-[0.95]"
                                                    style={{ borderRadius: "9999px", border: "1.5px solid rgba(255,255,255,0.3)", background: "transparent", color: "white" }}
                                                >
                                                    Close
                                                </Button>
                                            </div>
                                        </motion.div>
                                    ) : null}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </>
        );
    }

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4 text-left">
                        <motion.div
                            initial={{ y: "100%", opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{
                                type: "spring",
                                damping: 25,
                                stiffness: 200,
                            }}
                            className="w-full max-w-md bg-[#FFFBEB] dark:bg-[#1a1a1a] rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl border border-amber-100/20"
                        >
                            <div className="relative p-6 sm:p-8 text-center">
                                <button
                                    onClick={() => {
                                        handleProPoints(1, "record_only");
                                        onClose();
                                    }}
                                    className="absolute right-4 top-4 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                >
                                    <X className="h-5 w-5 text-muted-foreground" />
                                </button>

                                <div className="flex items-center justify-center gap-3 mb-6">
                                    <div className="h-12 w-12 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                        <Zap className="h-6 w-6 text-amber-600 fill-amber-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-[#0F172A] dark:text-white">
                                        Record Successful!
                                    </h2>
                                </div>

                                {showQR && orderId && isPro ? (
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        className="bg-white p-6 rounded-2xl flex flex-col items-center mb-8 shadow-inner border border-amber-100 mx-auto w-fit"
                                    >
                                        <QRCodeSVG
                                            value={invoiceUrl}
                                            size={180}
                                            imageSettings={{
                                                src: "/favicon.png",
                                                height: 36,
                                                width: 36,
                                                excavate: true,
                                            }}
                                        />
                                        <p className="mt-4 text-xs font-bold text-amber-600 tracking-wider">
                                            SCAN TO DOWNLOAD INVOICE
                                        </p>
                                    </motion.div>
                                ) : (
                                    <div className="relative p-6 rounded-2xl border-2 border-dashed border-amber-200/50 dark:border-amber-800/30 bg-white/50 dark:bg-black/20 mb-8 text-left">
                                        <div className="flex items-center justify-between mb-4 text-left">
                                            <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">
                                                Total Bill
                                            </span>
                                            <span className="text-3xl font-black text-red-700 dark:text-red-500">
                                                ₹{finalTotal.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="space-y-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar text-left">
                                            {discountedItems.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex justify-between items-center text-sm"
                                                >
                                                    <span className="text-[#475569] dark:text-slate-400 font-medium">
                                                        {item.quantity}x{" "}
                                                        {item.productName}
                                                    </span>
                                                    <span className="text-muted-foreground font-medium">
                                                        ₹{Number(item.visualTotal).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                            {discount > 0 && (
                                                <div className="flex justify-between items-center text-sm pt-2 border-t border-amber-100 dark:border-amber-900/30">
                                                    <span className="text-green-600 font-bold italic">
                                                        Discount Applied
                                                    </span>
                                                    <span className="text-green-600 font-bold">
                                                        -₹{discount.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            {isPro &&
                                                isGstEnabled &&
                                                items.some((item) => {
                                                    const product =
                                                        products.find(
                                                            (p) =>
                                                                p.id ===
                                                                item.productId,
                                                        );
                                                    return (
                                                        ((product as any)
                                                            ?.gstRate || 0) > 0
                                                    );
                                                }) && (
                                                    <div className="flex justify-between items-center text-sm pt-1">
                                                        <span className="text-amber-600 font-bold">
                                                            Total GST
                                                        </span>
                                                        <span className="text-amber-600 font-bold">
                                                            ₹{uiTotalGst.toFixed(2)}
                                                        </span>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <AnimatePresence mode="wait">
                                        {!showPhoneInput ? (
                                            <motion.div
                                                key="share-btn"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                className="flex flex-col gap-3"
                                            >
                                                {isPro && (
                                                    <Button
                                                        onClick={() => {
                                                            handleProPoints(1, "print_invoice");
                                                            setShowPrintPreview(true);
                                                        }}
                                                        className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold text-lg shadow-lg shadow-primary/20 dark:shadow-none transition-all active:scale-[0.98]"
                                                    >
                                                        <FileText className="h-5 w-5 mr-2" />
                                                        Print Invoice
                                                    </Button>
                                                )}
                                                <Button
                                                    onClick={() =>
                                                        handleWhatsAppAction()
                                                    }
                                                    className="w-full h-14 rounded-2xl bg-[#22C55E] hover:bg-[#1ea34d] text-white font-bold text-lg shadow-lg shadow-green-200 dark:shadow-none transition-all active:scale-[0.98]"
                                                >
                                                    <MessageSquare className="h-5 w-5 mr-2" />
                                                    Share on WhatsApp
                                                </Button>
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="phone-input"
                                                initial={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                className="flex gap-2"
                                            >
                                                <Input
                                                    type="tel"
                                                    placeholder="Customer 10-digit number"
                                                    value={phoneNumber}
                                                    onChange={(e) =>
                                                        setPhoneNumber(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="h-14 rounded-2xl border-2 border-[#22C55E] bg-white text-lg focus-visible:ring-0"
                                                    autoFocus
                                                    maxLength={10}
                                                />
                                                <Button
                                                    onClick={
                                                        handleWhatsAppAction
                                                    }
                                                    disabled={
                                                        phoneNumber.replace(
                                                            /\D/g,
                                                            "",
                                                        ).length < 10
                                                    }
                                                    className="h-14 w-14 rounded-2xl bg-[#22C55E] hover:bg-[#1ea34d] p-0 flex-shrink-0"
                                                >
                                                    <Send className="h-6 w-6" />
                                                </Button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {orderId &&
                                        isPro &&
                                        !isFromTransactions && (
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    handleProPoints(1, "show_qr");
                                                    if (
                                                        !showQR &&
                                                        currentOrderId
                                                    ) {
                                                        publishInvoice(currentOrderId).then(url => { if (url) setShortUrl(url); }).catch(() => { });
                                                    }
                                                    setShowQR(!showQR);
                                                    setShowPhoneInput(false);
                                                }}
                                                className="w-full h-14 rounded-2xl border-amber-200 text-amber-700 font-bold hover:bg-amber-50"
                                            >
                                                <QrCode className="h-5 w-5 mr-2" />
                                                {showQR
                                                    ? "Show Bill Summary"
                                                    : "Show Customer QR"}
                                            </Button>
                                        )}

                                    <Button
                                        variant="ghost"
                                        onClick={() => {
                                            handleProPoints(1, "record_only");
                                            onClose();
                                        }}
                                        className="w-full h-14 rounded-2xl font-bold text-[#64748B] hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
}